import { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  ShieldCheck, 
  Smartphone, 
  ArrowRight, 
  Menu, 
  X, 
  Lock,
  DollarSign
} from 'lucide-react';

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Replace this with your actual Telegram Link
  const TELEGRAM_LINK = "https://t.me/+cfUzetWULlw1OTY1";
  
  // Assuming the logo is in the public folder or same directory. 
  // In a real React app, you would import this or place it in /public
  const LOGO_URL = "logo.png"; 

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigateToTelegram = () => {
    // Fire Facebook Pixel 'Lead' event
    if (window.fbq) {
      window.fbq('track', 'Lead');
    }
    
    // Open Telegram
    window.open(TELEGRAM_LINK, '_blank');
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans selection:bg-emerald-500 selection:text-white">
      
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-slate-900/95 backdrop-blur-md shadow-lg border-b border-slate-800' : 'bg-transparent'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20"> {/* Increased height slightly for logo */}
            <div className="flex items-center space-x-3">
              <div className="h-12 w-12 rounded-full overflow-hidden border-2 border-emerald-500/20 shadow-lg shadow-emerald-500/10">
                <img 
                  src={LOGO_URL} 
                  alt="TradeLoom Fx Logo" 
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null; 
                    e.target.src = "https://placehold.co/100x100/020617/10b981?text=TL"; // Fallback if image not found
                  }}
                />
              </div>
              <span className="font-bold text-xl tracking-tight text-white hidden sm:block">
                TradeLoom <span className="text-emerald-500">Fx</span>
              </span>
            </div>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#benefits" className="text-slate-300 hover:text-emerald-400 transition-colors text-sm font-medium">Why Us?</a>
              <a href="#results" className="text-slate-300 hover:text-emerald-400 transition-colors text-sm font-medium">Results</a>
              {/* <a href="#faq" className="text-slate-300 hover:text-emerald-400 transition-colors text-sm font-medium">FAQ</a> */}
              <button 
                onClick={navigateToTelegram}
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-6 py-2 rounded-full font-bold transition-all transform hover:scale-105 shadow-[0_0_15px_rgba(16,185,129,0.5)]"
              >
                Join Free Channel
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-slate-300 hover:text-white">
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Nav Dropdown */}
        {isMenuOpen && (
          <div className="md:hidden bg-slate-900 border-b border-slate-800">
            <div className="px-4 pt-2 pb-6 space-y-2">
              <a onClick={() => setIsMenuOpen(false)} href="#benefits" className="block px-3 py-2 text-slate-300 hover:text-emerald-400">Why Us?</a>
              <a onClick={() => setIsMenuOpen(false)} href="#results" className="block px-3 py-2 text-slate-300 hover:text-emerald-400">Results</a>
              <button 
                onClick={navigateToTelegram}
                className="w-full mt-4 bg-emerald-500 text-slate-900 px-4 py-3 rounded-lg font-bold"
              >
                Join Now (Free)
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <div className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
        {/* Background Gradients */}
        <div className="absolute top-0 left-1/2 w-full -translate-x-1/2 h-full z-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/10 rounded-full blur-[100px]"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-600/10 rounded-full blur-[100px]"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-semibold mb-8 animate-fade-in-up">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            Live Signals Active Now
          </div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight mb-6 leading-tight">
            Stop Gambling. <br className="hidden md:block" />
            Start Trading with <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-emerald-600">Institutional Accuracy.</span>
          </h1>
          
          <p className="text-lg md:text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            Get exclusive access to high-probability Forex & Gold signals from TradeLoom Fx. 
            {/* <span className="text-white font-semibold"> 100% Free. No credit card required.</span> */}
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button 
              onClick={navigateToTelegram}
              className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-slate-950 font-bold text-lg rounded-xl shadow-[0_0_30px_rgba(16,185,129,0.4)] transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 group"
            >
              <Smartphone className="h-5 w-5" />
              Join Telegram for Free
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <div className="text-slate-500 text-sm mt-2 sm:mt-0">
              <span className="block sm:inline">Join 2,000+ Traders</span>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Strip */}
      <div className="border-y border-slate-800 bg-slate-900/50 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="space-y-1">
              <div className="text-3xl font-bold text-white">95%</div>
              <div className="text-slate-500 text-sm uppercase tracking-wider">Win Rate</div>
            </div>
            <div className="space-y-1">
              <div className="text-3xl font-bold text-white">2k+</div>
              <div className="text-slate-500 text-sm uppercase tracking-wider">Active Members</div>
            </div>
            <div className="space-y-1">
              <div className="text-3xl font-bold text-white">Daily</div>
              <div className="text-slate-500 text-sm uppercase tracking-wider">Market Analysis</div>
            </div>
            <div className="space-y-1">
              <div className="text-3xl font-bold text-white">XAUUSD</div>
              <div className="text-slate-500 text-sm uppercase tracking-wider">Gold Specialists</div>
            </div>
          </div>
        </div>
      </div>

      {/* Pain vs Pleasure (Why Us) */}
      <div id="benefits" className="py-20 bg-slate-950">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Most Traders Fail (And How We Fix It)</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">Trading alone is difficult. Trading with TradeLoom Fx experts changes the game completely.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-slate-900 p-8 rounded-2xl border border-slate-800 hover:border-emerald-500/50 transition-colors group">
              <div className="h-12 w-12 bg-slate-800 rounded-lg flex items-center justify-center mb-6 group-hover:bg-emerald-500 transition-colors">
                <TrendingUp className="h-6 w-6 text-emerald-500 group-hover:text-slate-900" />
              </div>
              <h3 className="text-xl font-bold mb-3">Sniper Entries</h3>
              <p className="text-slate-400">Stop entering too early or chasing the market. We provide exact Entry, Take Profit, and Stop Loss levels.</p>
            </div>

            {/* Feature 2 */}
            <div className="bg-slate-900 p-8 rounded-2xl border border-slate-800 hover:border-emerald-500/50 transition-colors group">
              <div className="h-12 w-12 bg-slate-800 rounded-lg flex items-center justify-center mb-6 group-hover:bg-emerald-500 transition-colors">
                <ShieldCheck className="h-6 w-6 text-emerald-500 group-hover:text-slate-900" />
              </div>
              <h3 className="text-xl font-bold mb-3">Risk Management</h3>
              <p className="text-slate-400">Signals are worthless if you blow your account. We teach you proper lot sizing to protect your capital.</p>
            </div>

            {/* Feature 3 */}
            <div className="bg-slate-900 p-8 rounded-2xl border border-slate-800 hover:border-emerald-500/50 transition-colors group">
              <div className="h-12 w-12 bg-slate-800 rounded-lg flex items-center justify-center mb-6 group-hover:bg-emerald-500 transition-colors">
                <Lock className="h-6 w-6 text-emerald-500 group-hover:text-slate-900" />
              </div>
              <h3 className="text-xl font-bold mb-3">VIP Analysis Free</h3>
              <p className="text-slate-400">Get institutional-grade analysis for Gold, Crypto, and Forex completely free in our public channel.</p>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-20 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">How to Start Profiting in 3 Steps</h2>
          
          <div className="space-y-8">
            <div className="flex md:items-center gap-6">
              <div className="flex-shrink-0 h-12 w-12 rounded-full bg-emerald-500 flex items-center justify-center font-bold text-slate-900 text-xl">1</div>
              <div className="bg-slate-950 p-6 rounded-xl border border-slate-800 flex-grow">
                <h3 className="font-bold text-lg text-white">Click the Button</h3>
                <p className="text-slate-400">Hit any of the "Join Telegram" buttons on this page. It's completely free.</p>
              </div>
            </div>

            <div className="flex md:items-center gap-6">
              <div className="flex-shrink-0 h-12 w-12 rounded-full bg-emerald-500 flex items-center justify-center font-bold text-slate-900 text-xl">2</div>
              <div className="bg-slate-950 p-6 rounded-xl border border-slate-800 flex-grow">
                <h3 className="font-bold text-lg text-white">Join the Channel</h3>
                <p className="text-slate-400">You'll be taken to Telegram. Click "Join" at the bottom of the screen.</p>
              </div>
            </div>

            <div className="flex md:items-center gap-6">
              <div className="flex-shrink-0 h-12 w-12 rounded-full bg-emerald-500 flex items-center justify-center font-bold text-slate-900 text-xl">3</div>
              <div className="bg-slate-950 p-6 rounded-xl border border-slate-800 flex-grow">
                <h3 className="font-bold text-lg text-white">Copy & Paste</h3>
                <p className="text-slate-400">Wait for the notification, copy the numbers into your MetaTrader 4/5, and watch the trade run.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Social Proof / Testimonials */}
      <div id="results" className="py-20 bg-slate-950">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">What Our Members Say</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-slate-900 p-6 rounded-2xl border border-slate-800 relative">
                <div className="absolute -top-4 -left-4 bg-emerald-500 rounded-full p-2">
                   <DollarSign className="h-5 w-5 text-slate-900" />
                </div>
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-emerald-500 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                  ))}
                </div>
                <p className="text-slate-300 italic mb-4">"Honestly, I was skeptical because it's free. But after 2 weeks I'm up 15% on my account. The TradeLoom Fx analysis is spot on."</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center font-bold text-slate-500">
                    {['JD', 'MS', 'AK'][i-1]}
                  </div>
                  <div>
                    <div className="font-bold text-white">Member #{2400 + i * 45}</div>
                    {/* <div className="text-xs text-slate-500">Joined via Facebook</div> */}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="py-20 bg-gradient-to-b from-slate-900 to-slate-950 border-t border-slate-800">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Don't Miss The Next Move</h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">The market is moving right now. Join the channel to get the next signal sent directly to your phone.</p>
          <button 
            onClick={navigateToTelegram}
            className="w-full sm:w-auto px-10 py-5 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-extrabold text-xl rounded-xl shadow-[0_0_40px_rgba(16,185,129,0.4)] transition-all transform hover:scale-105"
          >
            Get Free Signals Now
          </button>
          <p className="mt-4 text-sm text-slate-500">Limited time free access</p>
        </div>
      </div>

      {/* Footer & Disclaimer */}
      <footer className="bg-black py-12 border-t border-slate-900">
        <div className="max-w-6xl mx-auto px-4 text-center md:text-left">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-1 md:col-span-2">
              <span className="font-bold text-xl text-white mb-4 block">TradeLoom <span className="text-emerald-500">Fx</span></span>
              <p className="text-slate-500 text-sm max-w-sm">
                Providing high-quality educational analysis and market insights for Forex and Gold traders worldwide.
              </p>
            </div>
            <div>
              
            </div>
            <div>
              
            </div>
          </div>
          
          <div className="border-t border-slate-900 pt-8">
            <p className="text-xs text-slate-600 text-justify leading-relaxed">
              <strong>Risk Warning:</strong> Trading Foreign Exchange (Forex) and Contracts for Differences (CFD) on margin carries a high level of risk and may not be suitable for all investors. The high degree of leverage can work against you as well as for you. Before deciding to trade Forex, you should carefully consider your investment objectives, level of experience, and risk appetite. The possibility exists that you could sustain a loss of some or all of your initial investment and therefore you should not invest money that you cannot afford to lose. You should be aware of all the risks associated with foreign exchange trading and seek advice from an independent financial advisor if you have any doubts. Past performance is not indicative of future results. The content on this website and the Telegram channel is for educational purposes only and does not constitute financial advice.
            </p>
            <p className="text-center text-slate-700 text-xs mt-8">
              &copy; {new Date().getFullYear()} TradeLoom Fx. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;